import { NoValue } from 'components/no_value/NoValue';
import { ThemePickerDropdownItem } from 'components/top_bar/TopBarThemePicker';
import { useTheme } from 'contexts/ThemeContext';
import { useRef } from 'react';
import { Control, Controller } from 'react-hook-form';
import { HoverTooltip } from 'shared/hover_tooltip/HoverTooltip';
import styled from 'styled-components';
import { ThemeType } from 'styles/colorMode';
import { tokens } from 'styles/tokens';
import { usePendoTrackTheme } from 'styles/usePendoTrackTheme';
import {
    ProfileDropdownOption,
    ProfileItem,
    ProfileSettingDropdown,
    ProfileSubSection,
} from '../components';
import { UserSettingsFormData } from './UserProfileModal';

const themeOptions: ProfileDropdownOption<ThemeType>[] = [
    { Value: ThemeType.light, Text: 'Light' },
    { Value: ThemeType.darkOriginal, Text: 'Dark' },
    { Value: ThemeType.dark, Text: 'Navy' },
    { Value: ThemeType.night, Text: 'Night' },
];

export type AppearanceOption = 'theme';

type Props = Readonly<{
    control: Control<UserSettingsFormData>;
    allowedFeatures: Set<AppearanceOption>;
}>;

const SdLabsWrapper = styled.div`
    border-radius: 8px;
    --col: rgb(from ${tokens.success.default} r g b / 1);
    border: 1px solid var(--col);
    background-color: rgb(from ${tokens.success.light} r g b / 0.2);
    padding-inline: 0.5rem;
    width: fit-content;
    color: var(--col);
    font-weight: 300;
`;

function SdLabs({ featureName }: { featureName: string }) {
    const anchorRef = useRef<HTMLDivElement>(null);
    return (
        <>
            <SdLabsWrapper tabIndex={0} ref={anchorRef}>
                Preview
            </SdLabsWrapper>
            <HoverTooltip anchorRef={anchorRef} focusable>
                <p style={{ margin: 0 }}>
                    <strong>{featureName}</strong> is in preview, offering an early look
                    at the feature before it is finalized.
                </p>
                <p style={{ margin: 0 }}>
                    Some behaviors may change as we continue to refine the feature.
                </p>
            </HoverTooltip>
        </>
    );
}

export function AppearanceSubSection({ control, allowedFeatures }: Props) {
    const { setColorMode, colorMode } = useTheme();
    const trackTheme = usePendoTrackTheme();

    if (allowedFeatures.size === 0) {
        return null;
    }

    return (
        <ProfileSubSection title="Appearance">
            {allowedFeatures.has('theme') && (
                <ProfileItem
                    label={
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                            }}
                        >
                            Color Theme <SdLabs featureName="Color Theme" />
                        </div>
                    }
                >
                    <Controller
                        control={control}
                        name="colorMode"
                        render={({ field: { onChange, value } }) => (
                            <ProfileSettingDropdown
                                options={themeOptions}
                                value={
                                    themeOptions.find((o) => o.Value === value) ??
                                    themeOptions[0]
                                }
                                renderItem={(val) => (
                                    <ThemePickerDropdownItem item={val.item.Value} />
                                )}
                                renderLabel={(val) =>
                                    val.value ? (
                                        <ThemePickerDropdownItem item={val.value.Value} />
                                    ) : (
                                        <NoValue />
                                    )
                                }
                                onConfirm={(value) => {
                                    trackTheme(value.Value, colorMode, 'settings_modal');
                                    setColorMode(value.Value, {
                                        transient: true,
                                    });
                                    onChange(value.Value);
                                }}
                            />
                        )}
                    />
                </ProfileItem>
            )}
        </ProfileSubSection>
    );
}
